import { attachRouterTracing } from './sentry';

// eslint-disable-next-line no-console -- deliberate release stamp in every console
console.info('[build]', __COMMIT_HASH__);

// Load-bearing against zod's sideEffects:false tree-shaking — mechanism and
// pin: scripts/check-zod-locale.mts (runs after every build).
z.config(z.locales.en());
import '@rainbow-me/rainbowkit/styles.css';
import './tailwind.css';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  RainbowKitProvider,
  darkTheme,
  lightTheme,
} from '@rainbow-me/rainbowkit';
import type { ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { z } from 'zod';
import { track } from '@/shared/utils/track';
import '@/shared/utils/posthog';
import { wagmiConfig } from '@/shared/utils/wagmiConfig';
import { router } from './routes';

attachRouterTracing(router);
import ColorThemeProvider, { useColorTheme } from './ColorThemeProvider';
import UiError from './UiError';

function ThemedRainbowKit({ children }: { children: ReactNode }) {
  const { isDark } = useColorTheme();
  return (
    <RainbowKitProvider theme={isDark ? darkTheme() : lightTheme()}>
      {children}
    </RainbowKitProvider>
  );
}

const queryClient = new QueryClient();

async function startApp() {
  if (import.meta.env.VITE_MOCK === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  const rootEl = document.getElementById('root');
  if (rootEl === null) throw new Error('#root missing from index.html');
  createRoot(rootEl).render(
    <Sentry.ErrorBoundary
      fallback={<UiError />}
      onError={() => track('error_page_shown')}
    >
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <ColorThemeProvider>
            <ThemedRainbowKit>
              <RouterProvider router={router} />
            </ThemedRainbowKit>
          </ColorThemeProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </Sentry.ErrorBoundary>,
  );
}

void startApp();
